/**
 * useNews — hook for news intelligence data fetching.
 *
 * Features:
 *  - Article list + dashboard stats fetching
 *  - Auto-refresh every 5 minutes (configurable)
 *  - Exponential-backoff retry on failure (up to 3 retries)
 *  - Refreshing state for button animation
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { newsIntelAPI } from '../../../services/api';
import { normalizeArticleDetailResponse, normalizeNewsArticle } from '../shared';

const AUTO_REFRESH_MS = 5 * 60 * 1000; // 5 minutes
const MAX_RETRIES = 3;
const BASE_DELAY_MS = 2000;
const CACHE_TTL_MS = 5 * 60 * 1000;
const LIST_CACHE_PREFIX = 'sagax_news_list_cache_v1:';
const DASHBOARD_CACHE_KEY = 'sagax_news_dashboard_cache_v1';

const newsMemoryCache = {
  lists: new Map(),
  dashboard: null,
  dashboardTs: 0,
};

const newsInflight = {
  lists: new Map(),
  dashboard: null,
};

function getListCacheKey(category = '') {
  return `${LIST_CACHE_PREFIX}${category || '__all__'}`;
}

function readSessionCache(key) {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || (Date.now() - parsed.ts) > CACHE_TTL_MS) {
      window.sessionStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeSessionCache(key, payload) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(payload));
  } catch {
    // ignore storage failures
  }
}

function getCachedArticles(category = '') {
  const memory = newsMemoryCache.lists.get(category);
  if (memory && (Date.now() - memory.ts) <= CACHE_TTL_MS) {
    return memory;
  }
  const session = readSessionCache(getListCacheKey(category));
  if (session?.articles) {
    newsMemoryCache.lists.set(category, session);
    return session;
  }
  return null;
}

function setCachedArticles(category = '', articles = []) {
  const payload = { articles, ts: Date.now() };
  newsMemoryCache.lists.set(category, payload);
  writeSessionCache(getListCacheKey(category), payload);
  return payload;
}

function getCachedDashboard() {
  if (newsMemoryCache.dashboard && (Date.now() - newsMemoryCache.dashboardTs) <= CACHE_TTL_MS) {
    return newsMemoryCache.dashboard;
  }
  const session = readSessionCache(DASHBOARD_CACHE_KEY);
  if (session?.dashboard) {
    newsMemoryCache.dashboard = session.dashboard;
    newsMemoryCache.dashboardTs = session.ts;
    return session.dashboard;
  }
  return null;
}

function setCachedDashboard(dashboard) {
  const ts = Date.now();
  newsMemoryCache.dashboard = dashboard;
  newsMemoryCache.dashboardTs = ts;
  writeSessionCache(DASHBOARD_CACHE_KEY, { dashboard, ts });
  return dashboard;
}

/** Exponential backoff helper */
async function withRetry(fn, retries = MAX_RETRIES) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      if (attempt === retries) throw err;
      const delay = BASE_DELAY_MS * 2 ** attempt;
      await new Promise((r) => setTimeout(r, delay));
    }
  }
}

async function fetchArticlesRequest(category = '') {
  const sd = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10).replace(/-/g, '');
  const params = { start_date: sd };
  if (category) params.category = category;
  const res = await withRetry(() => newsIntelAPI.listArticles(params));
  if (!res?.success) return [];
  const normalized = (res.articles || []).map(normalizeNewsArticle);
  setCachedArticles(category, normalized);
  return normalized;
}

async function fetchDashboardRequest() {
  const res = await withRetry(() => newsIntelAPI.getDashboard());
  if (!res?.success) return null;
  return setCachedDashboard(res.dashboard);
}

export async function prefetchNewsArticles(category = '', { force = false } = {}) {
  const cached = !force ? getCachedArticles(category) : null;
  if (cached?.articles?.length) return cached.articles;
  if (newsInflight.lists.has(category)) return newsInflight.lists.get(category);

  const request = fetchArticlesRequest(category)
    .finally(() => {
      newsInflight.lists.delete(category);
    });
  newsInflight.lists.set(category, request);
  return request;
}

export async function prefetchNewsDashboard({ force = false } = {}) {
  const cached = !force ? getCachedDashboard() : null;
  if (cached) return cached;
  if (newsInflight.dashboard) return newsInflight.dashboard;

  newsInflight.dashboard = fetchDashboardRequest()
    .finally(() => {
      newsInflight.dashboard = null;
    });
  return newsInflight.dashboard;
}

export async function prefetchNewsPageData() {
  const [articles, dashboard] = await Promise.all([
    prefetchNewsArticles(''),
    prefetchNewsDashboard(),
  ]);
  return { articles, dashboard };
}

export function useNews() {
  const initialArticlesCache = getCachedArticles('');
  const initialDashboardCache = getCachedDashboard();
  const [articles, setArticles] = useState(initialArticlesCache?.articles || []);
  const [dashboard, setDashboard] = useState(initialDashboardCache || null);
  const [loading, setLoading] = useState(!(initialArticlesCache?.articles?.length));
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articleDetail, setArticleDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const autoRefreshRef = useRef(null);

  const fetchArticles = useCallback(async (cat = category, options = {}) => {
    const { force = false, keepVisibleData = true } = options;
    const cached = !force ? getCachedArticles(cat) : null;
    if (cached?.articles?.length && keepVisibleData) {
      setArticles(cached.articles);
      setLoading(false);
    } else if (!keepVisibleData) {
      setLoading(true);
    } else if (!articles.length) {
      setLoading(true);
    }
    setError(null);
    try {
      const nextArticles = await prefetchNewsArticles(cat, { force });
      setArticles(nextArticles);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [articles.length, category]);

  const fetchDashboard = useCallback(async (options = {}) => {
    const { force = false } = options;
    const cached = !force ? getCachedDashboard() : null;
    if (cached) {
      setDashboard(cached);
    }
    try {
      const nextDashboard = await prefetchNewsDashboard({ force });
      if (nextDashboard) setDashboard(nextDashboard);
    } catch (e) {
      console.error('Dashboard fetch failed:', e);
    }
  }, []);

  const fetchArticleDetail = useCallback(async (articleId) => {
    setDetailLoading(true);
    try {
      const res = await newsIntelAPI.getArticle(articleId);
      if (res?.success) {
        const normalized = normalizeArticleDetailResponse(res);
        setArticleDetail(normalized);
        setSelectedArticle(normalized.article?.article_id || articleId);
      }
    } catch (e) {
      console.error('Article detail fetch failed:', e);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const changeCategory = useCallback((cat) => {
    setCategory(cat);
    fetchArticles(cat);
  }, [fetchArticles]);

  const closeDetail = useCallback(() => {
    setSelectedArticle(null);
    setArticleDetail(null);
  }, []);

  /** Manual refresh with animation flag */
  const refresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([
      fetchArticles(category, { force: true, keepVisibleData: true }),
      fetchDashboard({ force: true }),
    ]);
    setRefreshing(false);
  }, [category, fetchArticles, fetchDashboard]);

  /** Retry after error */
  const retry = useCallback(() => {
    setError(null);
    refresh();
  }, [refresh]);

  /* ── Initial fetch ── */
  useEffect(() => {
    fetchArticles(category, { keepVisibleData: true });
    fetchDashboard();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Auto-refresh timer (5 min) — pauses when tab is hidden ── */
  useEffect(() => {
    const tick = () => {
      if (!document.hidden) {
        fetchArticles(category, { force: true, keepVisibleData: true });
        fetchDashboard({ force: true });
      }
    };
    autoRefreshRef.current = setInterval(tick, AUTO_REFRESH_MS);
    return () => clearInterval(autoRefreshRef.current);
  }, [category, fetchArticles, fetchDashboard]);

  return {
    articles,
    dashboard,
    loading,
    refreshing,
    error,
    category,
    selectedArticle,
    articleDetail,
    detailLoading,
    changeCategory,
    fetchArticleDetail,
    closeDetail,
    refresh,
    retry,
  };
}

export default useNews;
